import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { acceptInvitation } from '../../api/invitations';
import { useTranslation } from 'react-i18next';
import styles from './AcceptInvitePage.module.css';

export function AcceptInvitePage() {
  const { t } = useTranslation(['admin', 'common']);
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('common:passwordsDoNotMatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('common:passwordMinLength'));
      return;
    }

    if (!token) {
      setError(t('invalidInvitation'));
      return;
    }

    setLoading(true);

    try {
      // Set tenant slug for the API call
      const store = useAuthStore.getState();
      store.setAuth(
        { id: '', email: '', full_name: '', role: '', preferred_locale: 'fr', organization_id: null },
        '',
        '',
        tenantSlug,
      );

      const data = await acceptInvitation({
        token,
        full_name: fullName,
        password,
      });

      // Auto-login with returned tokens
      const payload = JSON.parse(atob(data.access_token.split('.')[1]));
      setAuth(
        {
          id: payload.sub,
          email: payload.email,
          full_name: fullName,
          role: payload.role,
          preferred_locale: 'fr',
          organization_id: payload.organization_id ?? null,
        },
        data.access_token,
        data.refresh_token,
        tenantSlug,
      );
      navigate('/app');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg || t('failedToAcceptInvitation'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={handleSubmit}>
        <h1 className={styles.title}>{t('joinTitle')}</h1>
        <p className={styles.subtitle}>
          {t('joinDescription')}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.field}>
          <label>{t('organization')}</label>
          <input
            type="text"
            value={tenantSlug}
            onChange={(e) => setTenantSlug(e.target.value)}
            placeholder={t('orgPlaceholder')}
            required
          />
        </div>

        <div className={styles.field}>
          <label>{t('common:fullName')}</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t('fullNamePlaceholder')}
            required
            autoComplete="name"
          />
        </div>

        <div className={styles.field}>
          <label>{t('common:password')}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('passwordHint')}
            required
            autoComplete="new-password"
          />
        </div>

        <div className={styles.field}>
          <label>{t('common:confirmPassword')}</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('repeatPassword')}
            required
            autoComplete="new-password"
          />
        </div>

        <button type="submit" className={styles.submitBtn} disabled={loading}>
          {loading ? t('settingUp') : t('createAccount')}
        </button>
      </form>
    </div>
  );
}
